/**
 * Invite User Modal
 * Allows Company administrators to invite Manager or Staff users
 */

const COUNTRY_CODES = [
  { code: '+353', flag: '🇮🇪', country: 'Ireland' },
  { code: '+44', flag: '🇬🇧', country: 'United Kingdom' },
  { code: '+1', flag: '🇺🇸', country: 'United States' },
  { code: '+33', flag: '🇫🇷', country: 'France' },
  { code: '+49', flag: '🇩🇪', country: 'Germany' },
  { code: '+39', flag: '🇮🇹', country: 'Italy' },
  { code: '+34', flag: '🇪🇸', country: 'Spain' },
  { code: '+31', flag: '🇳🇱', country: 'Netherlands' },
  { code: '+32', flag: '🇧🇪', country: 'Belgium' },
  { code: '+46', flag: '🇸🇪', country: 'Sweden' },
  { code: '+47', flag: '🇳🇴', country: 'Norway' },
  { code: '+45', flag: '🇩🇰', country: 'Denmark' },
  { code: '+358', flag: '🇫🇮', country: 'Finland' },
  { code: '+41', flag: '🇨🇭', country: 'Switzerland' },
  { code: '+43', flag: '🇦🇹', country: 'Austria' },
  { code: '+48', flag: '🇵🇱', country: 'Poland' },
  { code: '+61', flag: '🇦🇺', country: 'Australia' },
  { code: '+64', flag: '🇳🇿', country: 'New Zealand' },
  { code: '+81', flag: '🇯🇵', country: 'Japan' },
  { code: '+82', flag: '🇰🇷', country: 'South Korea' },
  { code: '+91', flag: '🇮🇳', country: 'India' },
  { code: '+55', flag: '🇧🇷', country: 'Brazil' },
  { code: '+52', flag: '🇲🇽', country: 'Mexico' },
];

import { httpsCallable } from 'firebase/functions';
import { useCallback, useState, useEffect, type FormEvent } from 'react';
import type { Company, UserProfile } from '../../../../../shared/types';
import { functions } from '../../../config/firebase';
import { useInviteUser, type InviteUserFormData } from '../../../hooks/useInviteUser';
import { getCompanyUsers } from '../../../services/user.service';
import { AlertBox } from '../../common/AlertBox';
import { BaseModal } from '../../common/BaseModal';
import { UserTable } from '../../common/UserTable';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { FormInputField } from '../../form/FormInputField';
import { FormSelectField } from '../../form/FormSelectField';
import { SELECT_CHEVRON_STYLE } from '../../../styles/components';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company;
  currentUserCount: number;
  companyAdminEmail: string;
  companyAdminName: string;
  onSuccess: () => void;
}

export function InviteUserModal({
  isOpen,
  onClose,
  company,
  currentUserCount,
  companyAdminEmail,
  companyAdminName,
  onSuccess,
}: InviteUserModalProps) {
  const { inviteUser, loading, error } = useInviteUser(company);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<InviteUserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    countryCode: '+353',
    phoneNumber: '',
    userType: 'staff',
    managedStaffIds: [],
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailSendFailed, setEmailSendFailed] = useState(false);
  const [availableStaff, setAvailableStaff] = useState<UserProfile[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);

  // Load available staff members when modal opens
  useEffect(() => {
    if (isOpen) {
      loadStaff();
    }
  }, [isOpen]);

  const loadStaff = async () => {
    setLoadingStaff(true);
    try {
      const users = await getCompanyUsers(company.id, false);
      const staffMembers = users.filter(user => user.userType === 'staff');
      setAvailableStaff(staffMembers);
    } catch (error) {
      console.error('Error loading staff:', error);
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleClose = useCallback(() => {
    // Reset form
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      countryCode: '+353',
      phoneNumber: '',
      userType: 'staff',
      managedStaffIds: [],
    });
    setShowSuccess(false);
    setEmailSendFailed(false);
    onClose();
    if (showSuccess) {
      onSuccess();
    }
  }, [showSuccess, onClose, onSuccess]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Trim all inputs
      const trimmedFormData: InviteUserFormData = {
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phoneNumber: formData.phoneNumber?.trim(),
        userType: formData.userType,
        managedStaffIds: formData.managedStaffIds,
      };

      const result = await inviteUser(trimmedFormData);

      if (result.success && result.password) {
        // Send invitation email automatically
        try {
          const sendInvitationEmail = httpsCallable(functions, 'sendInvitationEmail');
          await sendInvitationEmail({
            toEmail: trimmedFormData.email,
            toFirstName: trimmedFormData.firstName,
            temporaryPassword: result.password,
            fromEmail: companyAdminEmail,
            fromName: companyAdminName,
            companyName: company.name,
            userRole: trimmedFormData.userType === 'manager' ? 'Manager' : 'Staff',
          });
          setEmailSendFailed(false);
        } catch (emailError) {
          console.error('Failed to send user invitation email:', emailError);
          // Don't block the flow if email fails - user is already created
          setEmailSendFailed(true);
        }

        setShowSuccess(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Success screen
  if (showSuccess) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title="User Invited Successfully!"
        size="small"
      >
        <div style={{ marginBottom: '24px' }}>
          <UserTable
            rows={[
              { label: 'Name', value: `${formData.firstName} ${formData.lastName}` },
              { label: 'Login Email', value: formData.email },
              { label: 'Role', value: formData.userType === 'manager' ? 'Manager' : 'Staff' },
            ]}
            columns={[
              { render: (row) => <span style={{ color: '#6b7280', width: '80px', display: 'inline-block' }}>{row.label}</span> },
              { render: (row) => <span style={{ color: '#111827', fontWeight: '500', wordBreak: 'break-all' }}>{row.value}</span> },
            ]}
          />

          <div style={{ marginTop: '12px' }}>
            {emailSendFailed ? (
            <AlertBox
              type="warning"
              title="Email Failed to Send"
              message={
                <>
                  The user account was created successfully, but the invitation email could not be sent to <strong>{formData.email}</strong>.
                  <br /><br />
                  Please manually share the login credentials with the user, or try resending the invitation email later.
                </>
              }
            />
          ) : (
            <AlertBox
              type="success"
              title="Invitation Email Sent!"
              message={
                <>
                  An email with login credentials has been automatically sent to <strong>{formData.email}</strong>.
                  The user can use these credentials to log in and will be prompted to change their password on first login.
                </>
              }
            />
          )}
          </div>
        </div>

        <ModalButtonGroup
          onCancel={handleClose}
          onSubmit={handleClose}
          submitLabel="Close"
          hideCancel
        />
      </BaseModal>
    );
  }

  // Main form
  const isAtLimit = currentUserCount >= company.maxUsers;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite User"
      size="small"
      loading={loading}
    >
      {/* User Limit Display */}
      <div style={{
        backgroundColor: isAtLimit ? '#fef3c7' : '#dbeafe',
        border: `1px solid ${isAtLimit ? '#fbbf24' : '#3b82f6'}`,
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
          Account Usage
        </span>
        <span style={{ fontSize: '14px', fontWeight: '700', color: isAtLimit ? '#92400e' : '#1e40af' }}>
          {currentUserCount} / {company.maxUsers}
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <FormSelectField
          label="User Type"
          value={formData.userType}
          onChange={(value) => setFormData({ ...formData, userType: value as 'manager' | 'staff' })}
          options={[
            { value: 'staff', label: 'Staff' },
            { value: 'manager', label: 'Manager' },
          ]}
          required
        />

        <FormInputField
          label="First Name"
          value={formData.firstName}
          onChange={(value) => setFormData({ ...formData, firstName: value })}
          required
          disabled={loading}
        />

        <FormInputField
          label="Last Name"
          value={formData.lastName}
          onChange={(value) => setFormData({ ...formData, lastName: value })}
          required
          disabled={loading}
        />

        <FormInputField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(value) => setFormData({ ...formData, email: value })}
          required
          disabled={loading}
        />

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
            Phone Number
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              value={formData.countryCode}
              onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
              disabled={loading}
              style={{ ...SELECT_CHEVRON_STYLE, padding: '10px 36px 10px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '6px', width: '140px', flexShrink: 0, boxSizing: 'border-box' }}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input
              type="tel"
              value={formData.phoneNumber || ''}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value.replace(/[^\d\s\-().]/g, '') })}
              placeholder="86 123 4567"
              disabled={loading}
              style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '6px', flex: 1, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Staff Assignment - Only show for managers */}
        {formData.userType === 'manager' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              Assign Staff Members (Optional)
            </label>
            {loadingStaff ? (
              <div style={{ textAlign: 'center', padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                Loading staff...
              </div>
            ) : availableStaff.length === 0 ? (
              <div style={{
                padding: '12px',
                backgroundColor: '#f3f4f6',
                borderRadius: '6px',
                color: '#6b7280',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                No staff members available. You can assign staff later.
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                {availableStaff.map((staff) => (
                  <label
                    key={staff.uid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      backgroundColor: formData.managedStaffIds?.includes(staff.uid) ? '#eff6ff' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.managedStaffIds?.includes(staff.uid) || false}
                      onChange={(e) => {
                        const currentStaffIds = formData.managedStaffIds || [];
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            managedStaffIds: [...currentStaffIds, staff.uid],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            managedStaffIds: currentStaffIds.filter(id => id !== staff.uid),
                          });
                        }
                      }}
                      disabled={loading}
                      style={{
                        marginRight: '10px',
                        width: '16px',
                        height: '16px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                        {staff.firstName} {staff.lastName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {staff.email}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
              Selected: {formData.managedStaffIds?.length || 0} staff member{formData.managedStaffIds?.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {error && <AlertBox type="error" message={error} />}

        <ModalButtonGroup
          onCancel={onClose}
          submitLabel="Invite User"
          isLoading={submitting}
          loadingLabel="Inviting..."
          flex
        />
      </form>
    </BaseModal>
  );
}
